import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { isIndexablePlayBoardPath } from "../../config/playBoardSeo";
import {
    SITE_ORIGIN,
    buildCanonicalUrl,
    buildHreflangAlternates,
    getPuzzleListingSeo,
    getRobotsContent,
    normalizePathname,
} from "../../config/siteSeo";

/**
 * Per-route canonical + robots for Google Search Console.
 * Indexable jigsaw/sliding board play URLs use PlayBoardSeoHead on the page instead.
 */
export default function SeoHead() {
    const { pathname, search } = useLocation();

    if (isIndexablePlayBoardPath(pathname)) {
        return null;
    }

    const canonical = buildCanonicalUrl(pathname, search);
    const robots = getRobotsContent(pathname);
    const listingSeo = getPuzzleListingSeo(pathname);
    const hreflangAlternates = buildHreflangAlternates(pathname, search);
    const isHome = normalizePathname(pathname) === "/";
    const websiteLd = isHome
        ? {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "Puzzle Interact",
              url: SITE_ORIGIN,
              description:
                  "Original crossword, word search, hidden message, sliding, and jigsaw puzzles with illustrated stories.",
              publisher: {
                  "@type": "Organization",
                  name: "Jeux Puzzle Interact",
                  url: SITE_ORIGIN,
              },
          }
        : null;
    const softwareLd = isHome
        ? {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "Puzzle Interact",
              operatingSystem: "Web, iOS, Android",
              applicationCategory: "GameApplication",
              aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: "4.8",
                  reviewCount: "24",
              },
              offers: {
                  "@type": "Offer",
                  price: "0",
                  priceCurrency: "USD",
              },
              description:
                  "Word puzzles that use thematic networks to help learners master vocabulary through logic.",
          }
        : null;

    return (
        <Helmet>
            {listingSeo?.title ? <title>{listingSeo.title}</title> : null}
            {listingSeo?.description ? (
                <meta name="description" content={listingSeo.description} />
            ) : null}
            <link rel="canonical" href={canonical} />
            {hreflangAlternates.map(({ hreflang, href }) => (
                <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
            ))}
            <meta name="robots" content={robots} />
            {websiteLd ? (
                <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
            ) : null}
            {softwareLd ? (
                <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
            ) : null}
        </Helmet>
    );
}
